import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Fetcher } from '@this/src/util';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import OnlineApplication from '@this/src/domain/online_application/online_application';
import moment from 'moment';
import { OnlineApplicationView } from './shared/online_application_view';
import { onlineApplicationStyles } from './shared/styles';
import type { ApplicationData } from './shared/types';
import { ERROR_MESSAGES, API_ENDPOINTS } from './shared/types';

const OnlineApplicationComplete = () => {
  const { token } = useParams<{ token: string }>();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState<OnlineApplication | null>(null);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactTel, setContactTel] = useState('');
  const [startDesiredMonth, setStartDesiredMonth] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      if (!token) {
        history.push('/');
        return;
      }

      try {
        setLoading(true);
        const response = await Fetcher.get<{ success: boolean; data: ApplicationData; error?: string }>(
          API_ENDPOINTS.USER.COMPLETE(token)
        );

        if (response.success && response.data) {
          const app = new OnlineApplication(response.data);
          setApplication(app);

          const firstCompany = response.data.companies[0];
          if (firstCompany) {
            setContactName(firstCompany.contact_name || '');
            setContactEmail(firstCompany.contact_email || '');
            setContactTel(firstCompany.contact_tel || '');
          }

          if (response.data.start_desired_month) {
            const parsed = moment(response.data.start_desired_month);
            if (parsed.isValid()) {
              setStartDesiredMonth(parsed.format('YYYY年M月'));
            }
          }
        } else {
          console.error('Failed to fetch application data:', response.error);
          history.push('/');
          setErrorMessage(response.error || ERROR_MESSAGES.FETCH_FAILED);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        history.push('/');
        setErrorMessage(error.response?.data?.error || ERROR_MESSAGES.FETCH_FAILED);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, history]);

  if (loading) {
    return <Loading />;
  }

  if (!application) {
    return (
      <div style={{ ...onlineApplicationStyles.container.main, backgroundColor: 'none' }}>
        <div style={onlineApplicationStyles.message.error.container}>
          <p style={onlineApplicationStyles.message.error.text}>
            {errorMessage && (
              <>
                {errorMessage.split('\n').map((line, index) => (
                  <React.Fragment key={index}>
                    {line}
                    {index < errorMessage.split('\n').length - 1 && <br />}
                  </React.Fragment>
                ))}
                <br />
                {ERROR_MESSAGES.CONTACT_SUPPORT}
              </>
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={onlineApplicationStyles.container.main}>
      <div style={onlineApplicationStyles.message.success.container}>
        <h1 style={onlineApplicationStyles.message.success.title}>お申込みありがとうございました</h1>
        <p style={onlineApplicationStyles.message.success.text}>
          申込完了メールをお送りしました。
          <br />
          24時間経過しても通知が来ない場合は弊社担当者までお知らせください。
        </p>
      </div>

      <OnlineApplicationView
        application={application}
        contactName={contactName}
        contactEmail={contactEmail}
        contactTel={contactTel}
        startDesiredMonth={startDesiredMonth}
      />
    </div>
  );
};

export default OnlineApplicationComplete;
